import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const IMAGE_LIBRARY_QUERY_KEY = 'imageLibrary';

const IMAGE_LIBRARY_PAGE_SIZE = 30;

interface ImageLibraryQueryParams {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	/** Matched against the library name and the original file name */
	query?: string;
	source?: Api.ImageLibrarySource;
}

export function useImageLibraryQuery({ enabled = true, impersonationContext, query, source }: ImageLibraryQueryParams) {
	const userSession = useUserSession();

	return useInfiniteQuery({
		enabled,
		meta: {
			errorLogging: { source: 'ImageLibrary' },
			toastErrorMessage: 'Failed to load your image library',
		},
		queryKey: [IMAGE_LIBRARY_QUERY_KEY, impersonationContext, query, source],
		queryFn: async ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IImageLibraryItem>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'imageLibrary',
					queryParams: {
						pageToken: pageParam || null,
						pageSize: IMAGE_LIBRARY_PAGE_SIZE,
						query: query || null,
						source: source || null,
					},
				}),
				'GET'
			);
		},
		// Coerce to null: react-query only stops on null/undefined, so an empty-string token would page forever.
		getNextPageParam: lastPage => lastPage.pageToken || null,
		initialPageParam: null as string | null,
	});
}

export function invalidateImageLibraryQuery() {
	return reactQueryClient.invalidateQueries({ queryKey: [IMAGE_LIBRARY_QUERY_KEY] });
}

/**
 * Adapts a library entry to the attachment shape every image picker consumer expects. The library entry's
 * own id identifies the entry, so the attachment id has to come from the nested snapshot.
 */
export function imageLibraryItemToAttachment(item: Api.IImageLibraryItem): Api.IFileAttachment {
	return {
		fileName: item.fileAttachment.fileName,
		fileSize: item.fileAttachment.fileSize,
		id: item.fileAttachment.id,
		mimeType: item.mimeType,
		url: item.url,
	};
}

export function imageLibraryDisplayName(item: Api.IImageLibraryItem) {
	return item.displayName || item.fileAttachment?.fileName;
}
